import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ReactNode } from "react";
import { getDiagnosisById } from "#/api/diagnosis.api";
import { getMedication } from "#/api/medications.api";
import { getSymptom } from "#/api/symptoms.api";
import { getVisit } from "#/api/visits.api";
import { getVital } from "#/api/vitals.api";
import { getProvider } from "#/api/providers.api";
import DiagnosisItem from "#/components/diagnoses/diagnosis-item";
import MedicationItem from "#/components/medications/medication-item";
import RecordItem from "#/components/medical-records/record-item";
import ProviderDetailCard from "#/components/providers/provider-detail-card";
import SymptomItem from "#/components/symptoms/symptom-item";
import ThemedSpinner from "#/components/shared/spinner";
import Typography from "#/components/shared/typography";
import { useSetPageContext } from "#/hooks/PageContext";
import { MedicalRecordSection } from "#/lib/medicalRecords";

const recordConfigs: Record<
  MedicalRecordSection,
  {
    label: string;
    fetcher: (id: string) => Promise<any>;
    render: (record: any, prefetched?: any) => ReactNode;
  }
> = {
  symptoms: {
    label: "Symptom",
    fetcher: getSymptom,
    render: (record) => <SymptomItem symptom={record} />,
  },
  diagnoses: {
    label: "Diagnosis",
    fetcher: getDiagnosisById,
    render: (record) => <DiagnosisItem diagnosis={record} />,
  },
  medications: {
    label: "Medication",
    fetcher: getMedication,
    render: (record) => <MedicationItem medication={record} />,
  },
  visits: {
    label: "Visit",
    fetcher: getVisit,
    render: (record) => <RecordItem item={record} />,
  },
  vitals: {
    label: "Vital Sign",
    fetcher: getVital,
    render: (record) => <RecordItem item={record} />,
  },
  providers: {
    label: "Provider",
    fetcher: getProvider,
    render: (record, prefetched) => (
      <ProviderDetailCard provider={prefetched ?? record} />
    ),
  },
};

type DetailRouteParams = {
  recordType: MedicalRecordSection;
  recordId: string;
  userId?: string;
};

type LocationState = {
  record?: any;
};

const MedicalRecordDetailPage = () => {
  const { recordType, recordId } = useParams<DetailRouteParams>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | undefined;
  const prefetchedRecord = state?.record;

  const config = recordType ? recordConfigs[recordType] : undefined;

  const shouldFetch = Boolean(config && recordId);

  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["medical-record-detail", recordType, recordId],
    queryFn: () => config!.fetcher(recordId as string),
    enabled: shouldFetch,
    staleTime: 30_000,
  });

  const record = data ?? prefetchedRecord;

  useSetPageContext(record);

  if (!config) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Typography variant="h4" className="text-terracotta">
          Medical record type not found.
        </Typography>
      </div>
    );
  }

  if (isLoading && !record) {
    return (
      <div className="container mx-auto px-4 py-8">
        <ThemedSpinner size="lg" />
      </div>
    );
  }

  if ((isError || !record) && !isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Typography variant="h4" className="text-terracotta">
          Unable to load {config.label.toLowerCase()} details.
        </Typography>
        {error instanceof Error && (
          <Typography variant="body2" className="mt-2 text-brown-700">
            {error.message}
          </Typography>
        )}
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <button
        className="text-sm font-medium text-terracotta-600 hover:text-terracotta-700"
        onClick={() => navigate(-1)}
      >
        ← Back
      </button>

      <div className="space-y-2">
        <Typography variant="h2" className="text-terracotta-600">
          {config.label} Details
        </Typography>
        {isLoading && record && (
          <div className="flex items-center gap-2 text-sm text-brown-700">
            <ThemedSpinner size="sm" /> Refreshing…
          </div>
        )}
      </div>

      {config.render(record, prefetchedRecord)}
    </div>
  );
};

export default MedicalRecordDetailPage;
